import React, { useState } from 'react';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { sanitizeFilename } from '../../scripts/document.filename.script';
import { Company } from '../../models/company.model';
import { Order } from '../../models/order.model';
import { GlobalSwal } from '../../styles/swal.style';
import { OrderGroup } from '../../models/order.group.model';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { dateFormat } from '../../scripts/date.time.format.script';
import { accumulatedAmount } from '../../scripts/financial.calculation.script';
import { nameGroup } from '../../libraries/group.name.library';
import { formatAddress, formatContact } from '../../scripts/format.value.script';
import { normalizeStatus, statusDocumentValue } from '../../libraries/status.document.library';
import { OrderDA } from '../../services/admin/order.service';

import AppInputExportType from '../element/InputExportType';
import logo from '../../assets/themes/svg/logo-official.png';

export interface AppModalOrderExportProps {
  companyForUser: Company | null,
  grouped: {state: boolean, desc: string},
  orders: Order[] | null,
  orderGroups: OrderGroup[] | null,
  filter?: {interval: string, start_date: string, end_date: string, contact: string, status: string, expiration: string, sort_field: string, sort_mode: string, group: string, search: string},
  vatSubject?: boolean
};

const AppModalOrderExport: React.FunctionComponent<AppModalOrderExportProps> = ({companyForUser, grouped, orders, orderGroups, filter, vatSubject}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Orders Data')

    const lastCol = vatSubject ? 'K' : 'J'

    worksheet.addRow([])
    worksheet.mergeCells(`A1:${lastCol}1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logo, {responseType: 'arraybuffer'})

    let imageLogotipo = workbook.addImage({
      buffer: imageBuffer.data,
      extension: 'png'
    })

    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.companyListOfOrders}`])
    titleRow.font = {size: 18, bold: true}
    titleRow.getCell(1).alignment = {vertical:'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${titleRow.number}:${lastCol}${titleRow.number}`)
    worksheet.getRow(2).height = 30

    worksheet.addRow([])
    worksheet.mergeCells(`A3:${lastCol}3`)
    worksheet.getRow(3).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell( (cell) => {
      cell.alignment = {vertical:'distributed', horizontal: 'left'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })

    worksheet.getRow(4).height = 20

    tableData.forEach((item) => { 
      let bodyRow = worksheet.addRow(item) 

      if (grouped.state) {
        if (item.length === 1) {
          bodyRow.font = {bold: true}
        }
      }
    })

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          worksheet.mergeCells(`A${i + 5}:${lastCol}${i + 5}`)
        }
      }
    }

    worksheet.getColumn(1).width = 18;
    worksheet.getColumn(2).width = 30;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 35;
    worksheet.getColumn(5).width = 13;
    worksheet.getColumn(6).width = 13;
    worksheet.getColumn(7).width = 13;
    worksheet.getColumn(8).width = 15;
    worksheet.getColumn(9).width = 15;
    worksheet.getColumn(10).width = vatSubject ? 15 : 17;
    if (vatSubject) { worksheet.getColumn(11).width = 17; }

    workbook.xlsx.writeBuffer().then( data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Orders_of_${company.name.replaceAll(' ', '_')}.xlsx`);
    })
  }

  const filePdf = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    const rightIndices = vatSubject ? [7, 8, 9, 10] : [7, 8, 9]

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          tableData[i][0] = {colSpan: vatSubject ? 9 : 8, content: tableData[i][0], styles: {fontStyle: 'bold'}}
        } else {
          for (const idx of rightIndices) {
            tableData[i][idx] = {content: tableData[i][idx], styles: {halign: 'right'}}
          }
        }
      }
    } else {
      for (let i = 0; i < tableData.length; i++) {
        for (const idx of rightIndices) {
          tableData[i][idx] = {content: tableData[i][idx], styles: {halign: 'right'}}
        }
      }
    }

    document.addImage(logo, 'PNG', 290, 5, 30, 9)

    document.setFontSize(18)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.companyListOfOrders}`, 25, 20)

    autoTable(document, {head: tableTitle, body: tableData, startY: 25})

    document.save(`Orders_of_${sanitizeFilename(company.name).replaceAll(' ', '_')}.pdf`)
  }

  const buildTableData = (items: Order[]) => {
    const tableData: any[][] = []
    for (let order of items) {
      tableData.push([order.order_number, order.concerning, formatContact(order.customer), formatAddress(lang, order.delivery_address?.address ?? order.delivery_address), statusDocumentValue(lang, order.status), dateFormat(lang.code, order.valid_until), `${order.currency.name} (${order.currency.symbol})`, Number(order.amount.sub_total.toFixed(2)), Number(order.amount.rebate_total.toFixed(2)), ...(vatSubject ? [Number(order.amount.tax_total.toFixed(2))] : []), Number(order.amount.total.toFixed(2))])
    }
    tableData.push(['', '', '', '', '', '', '', Number(accumulatedAmount(items).subtotal.toFixed(2)), Number(accumulatedAmount(items).rebate.toFixed(2)), ...(vatSubject ? [Number(accumulatedAmount(items).tax.toFixed(2))] : []), Number(accumulatedAmount(items).total.toFixed(2))])
    return tableData
  }

  const executeOrderExport = async () => {
    setLoadIndicator('on')

    if (companyForUser && orders && orderGroups && formatExport.valid) {
      let tableTitle: string[][] = [[lang.labels.orderNumber.toUpperCase(), lang.labels.concerning.toUpperCase(), lang.labels.customer.toUpperCase(), lang.labels.deliveyAddress.toUpperCase(), lang.labels.status.toUpperCase(), lang.labels.expiration.toUpperCase(), lang.labels.currency.toUpperCase(), lang.labels.subTotal.toUpperCase(), lang.labels.rebateTotal.toUpperCase(), ...(vatSubject ? [lang.labels.taxTotal.toUpperCase()] : []), lang.labels.amountTotal.toUpperCase()]]
      let tableData: any[][] = []

      if (grouped.state) {
        for (let i = 0; i < orderGroups.length; i++) {
          tableData.push([`GROUP ${i + 1}: ${nameGroup(lang, grouped.desc, orderGroups[i].group_detail).toUpperCase()}`])
          for (let order of orderGroups[i].orders) {
            tableData.push([order.order_number, order.concerning, formatContact(order.customer), formatAddress(lang, order.delivery_address?.address ?? order.delivery_address), statusDocumentValue(lang, order.status), dateFormat(lang.code, order.valid_until), `${order.currency.name} (${order.currency.symbol})`, Number(order.amount.sub_total.toFixed(2)), Number(order.amount.rebate_total.toFixed(2)), ...(vatSubject ? [Number(order.amount.tax_total.toFixed(2))] : []), Number(order.amount.total.toFixed(2))])
          }
          tableData.push(['', '', '', '', '', '', '', Number(accumulatedAmount(orderGroups[i].orders).subtotal.toFixed(2)), Number(accumulatedAmount(orderGroups[i].orders).rebate.toFixed(2)), ...(vatSubject ? [Number(accumulatedAmount(orderGroups[i].orders).tax.toFixed(2))] : []), Number(accumulatedAmount(orderGroups[i].orders).total.toFixed(2))])
        }
      } else if (filter) {
        const response = await OrderDA.getOrders(companyForUser.id, filter.interval, filter.start_date, filter.end_date, filter.contact, filter.status, filter.expiration, filter.sort_field, filter.sort_mode, '', 1, 20, filter.search, true)
        if (response.status === 200) {
          const allItems = (response.data.items ?? response.data.orders ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) }))
          tableData = buildTableData(allItems)
        } else {
          tableData = buildTableData(orders)
        }
      } else {
        tableData = buildTableData(orders)
      }

      setTimeout( () => {
        switch (formatExport.value) {
          case 'excel':
            fileExcel(companyForUser, tableTitle, tableData)
            break;
          case  'pdf':
            filePdf(companyForUser, tableTitle, tableData)
            break;
        }

        setLoadIndicator('off')
        executeHideModalOrderExport()
      }, 1000)
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        legendInvalid('modal-order-export-legend-format-export-type-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalOrderExport = () => {
    modalHide('modal-order-export')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-order-export-legend-format-export-type-required')
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id="modal-order-export" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportOrders}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={"modal-order-export"} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalOrderExport}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeOrderExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalOrderExport;
